import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Bell, Check, X, Clock, UserMinus } from 'lucide-react';
import { useRequestStore, useToastStore, useShiftStore } from '../store';
import { Spinner, EmptyState, Badge, Avatar } from '../components/ui';
import { api } from '../utils/api';
import { SHIFT_TIME_SLOTS, type ShiftDropRequest } from '../types';

export function AdminRequestsPage() {
  const { requests, isLoading, fetchRequests } = useRequestStore();
  const { addToast } = useToastStore();
  const { fetchShifts } = useShiftStore();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [dropRequests, setDropRequests] = useState<ShiftDropRequest[]>([]);
  const [loadingDrops, setLoadingDrops] = useState(false);
  const [activeTab, setActiveTab] = useState<'requests' | 'drops'>('requests');

  useEffect(() => {
    fetchRequests();
    loadDropRequests();
  }, []);

  const loadDropRequests = async () => {
    setLoadingDrops(true);
    try {
      const result = await api.getDropRequests();
      if (result.success && result.data) {
        setDropRequests(result.data);
      }
    } catch (error) {
      console.error('Failed to load drop requests:', error);
    } finally {
      setLoadingDrops(false);
    }
  };

  const getTimeLabel = (startTime: string) => {
    return SHIFT_TIME_SLOTS.find(s => s.start === startTime)?.label || startTime;
  };

  const handleApprove = async (requestId: string) => {
    if (processingId) return;
    
    setProcessingId(requestId);
    try {
      const result = await api.approveRequest(requestId);
      if (result.success) {
        addToast('Request approved!', 'success');
        await Promise.all([fetchRequests(), fetchShifts()]);
      } else {
        addToast(result.error || 'Failed to approve request', 'error');
      }
    } catch (error) {
      console.error('Approve request error:', error);
      addToast('Failed to approve request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    if (processingId) return;
    
    setProcessingId(requestId);
    try {
      const result = await api.rejectRequest(requestId);
      if (result.success) {
        addToast('Request rejected', 'success');
        await fetchRequests();
      } else {
        addToast(result.error || 'Failed to reject request', 'error');
      }
    } catch (error) {
      console.error('Reject request error:', error);
      addToast('Failed to reject request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApproveDrop = async (requestId: string) => {
    if (processingId) return;
    
    setProcessingId(requestId);
    try {
      const result = await api.approveDropRequest(requestId);
      if (result.success) {
        addToast('Drop request approved!', 'success');
        // Refresh drop requests
        await loadDropRequests();
        // Refresh shifts immediately and again after a short delay to ensure fresh data
        await fetchShifts();
        setTimeout(() => {
          fetchShifts();
        }, 300);
      } else {
        addToast(result.error || 'Failed to approve drop request', 'error');
      }
    } catch (error) {
      console.error('Approve drop error:', error);
      addToast('Failed to approve drop request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectDrop = async (requestId: string) => {
    if (processingId) return;
    
    setProcessingId(requestId);
    try {
      const result = await api.rejectDropRequest(requestId);
      if (result.success) {
        addToast('Drop request rejected', 'success');
        await loadDropRequests();
      } else {
        addToast(result.error || 'Failed to reject drop request', 'error');
      }
    } catch (error) {
      console.error('Reject drop error:', error);
      addToast('Failed to reject drop request', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const safeRequests = (requests && Array.isArray(requests)) ? requests : [];
  const safeDropRequests = (dropRequests && Array.isArray(dropRequests)) ? dropRequests : [];

  if (isLoading && safeRequests.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const pendingRequests = safeRequests.filter(r => r.status === 'Pending');
  const pendingDrops = safeDropRequests.filter(r => r.status === 'Pending');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Shift Requests</h1>
        <p className="text-slate-500 mt-1">Review and manage pending shift requests and drop requests</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'requests'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Shift Requests
          {pendingRequests.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'requests' ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {pendingRequests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('drops')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'drops'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Drop Requests
          {pendingDrops.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'drops' ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {pendingDrops.length}
            </span>
          )}
        </button>
      </div>

      {/* Shift Requests Tab */}
      {activeTab === 'requests' && (
        pendingRequests.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<Bell className="w-12 h-12 mx-auto" />}
            title="No pending requests"
            description="All shift requests have been processed."
          />
        </div>
      ) : (
        <div className="grid gap-4">
          {pendingRequests.map(request => (
            <div key={request.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <Avatar name={request.user.display_name || request.user.username} />
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {request.user.display_name || request.user.username}
                    </h3>
                    <p className="text-slate-500 text-sm">{request.user.email}</p>
                    
                    <div className="mt-3 p-3 bg-slate-50 rounded-xl">
                      <p className="font-medium text-slate-800">
                        {format(new Date(request.shift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
                      </p>
                      <p className="text-slate-600 text-sm">
                        {getTimeLabel(request.shift.start_time)}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      Requested {format(new Date(request.request_time), 'MMM d, h:mm a')}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-3">
                  <Badge variant="warning">Pending</Badge>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleReject(request.id)}
                      disabled={processingId === request.id}
                      className="btn btn-danger btn-sm"
                    >
                      {processingId === request.id ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <X className="w-4 h-4" />
                          Reject
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleApprove(request.id)}
                      disabled={processingId === request.id}
                      className="btn btn-success btn-sm"
                    >
                      {processingId === request.id ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Approve
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Drop Requests Tab */}
      {activeTab === 'drops' && (
        loadingDrops ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        ) : pendingDrops.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<UserMinus className="w-12 h-12 mx-auto" />}
              title="No pending drop requests"
              description="All drop requests have been processed."
            />
          </div>
        ) : (
          <div className="grid gap-4">
            {pendingDrops.map(request => (
              <div key={request.id} className="card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <Avatar name={request.user?.display_name || request.user?.username || 'Unknown'} />
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {request.user?.display_name || request.user?.username}
                      </h3>
                      <p className="text-slate-500 text-sm">{request.user?.email}</p>
                      
                      <div className="mt-3 p-3 bg-red-50 rounded-xl border border-red-200">
                        <p className="font-medium text-slate-800 flex items-center gap-2">
                          <UserMinus className="w-4 h-4 text-red-600" />
                          Requesting to Drop Shift
                        </p>
                        {request.shift && (
                          <>
                            <p className="text-slate-600 text-sm mt-1">
                              {format(new Date(request.shift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
                            </p>
                            <p className="text-slate-600 text-sm">
                              {getTimeLabel(request.shift.start_time)}
                            </p>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        Requested {format(new Date(request.request_time), 'MMM d, h:mm a')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-3">
                    <Badge variant="warning">Pending</Badge>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRejectDrop(request.id)}
                        disabled={processingId === request.id}
                        className="btn btn-danger btn-sm"
                      >
                        {processingId === request.id ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <X className="w-4 h-4" />
                            Reject
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleApproveDrop(request.id)}
                        disabled={processingId === request.id}
                        className="btn btn-success btn-sm"
                      >
                        {processingId === request.id ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            Approve
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
